import { computed, Injectable, signal } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class SidenavStore {
  private readonly sCollapsedSignal = signal<boolean>(
    (() => {
      try {
        const stored = localStorage.getItem('spri_sidenav_collapsed');
        return stored === 'true';
      } catch {
        return false;
      }
    })()
  );

  private readonly openItemIdSignal = signal<string | null>(
    (() => {
      try {
        const stored = localStorage.getItem('spri_sidenav_open_item');
        return stored || null;
      } catch {
        return null;
      }
    })()
  );

  readonly isCollapsed = computed(() => this.sCollapsedSignal());
  readonly openItemId = computed(() => this.openItemIdSignal());

  public toggleCollapse(): void {
    const next = !this.sCollapsedSignal();
    this.sCollapsedSignal.set(next);
    if (next) {
      this.openItemIdSignal.set(null);
    } // when collapsing, close any submenu
    this.persistState(next);
  }

  public collapse(): void {
    this.sCollapsedSignal.set(true);
    this.openItemIdSignal.set(null);
    this.persistState(true);
  }

  public expand(): void {
    this.sCollapsedSignal.set(false);
    this.persistState(false);
  }

  public collapseWithoutPersist(): void {
    // Collapse without persisting to localStorage
    this.sCollapsedSignal.set(true);
    this.openItemIdSignal.set(null);
  }

  public expandAndOpen(id: string): void {
    this.sCollapsedSignal.set(false);
    this.openItemIdSignal.set(id);
    this.persistState(false);
  }

  public toggleItem(id: string): void {
    // If collapsed, expand first and then open the item
    if (this.sCollapsedSignal()) {
      this.expandAndOpen(id);
    } else {
      // If expanded, just toggle the item
      const currentOpenId = this.openItemIdSignal();
      if (currentOpenId === id) {
        this.openItemIdSignal.set(null);
      } else {
        this.openItemIdSignal.set(id);
      }
      this.persistOpenItemId(this.openItemIdSignal());
    }
  }

  private persistState(collapsed: boolean): void {
    try {
      localStorage.setItem('spri_sidenav_collapsed', collapsed.toString());
    } catch {
      // Ignore localStorage errors
    }
  }

  private persistOpenItemId(openItemId: string | null): void {
    try {
      if (openItemId) {
        localStorage.setItem('spri_sidenav_open_item', openItemId);
      } else {
        localStorage.removeItem('spri_sidenav_open_item');
      }
    } catch {
      // Ignore localStorage errors
    }
  }
}
